import React from "react";
import {
  Chip,
  Link,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TableSortLabel,
  useTheme,
  Theme,
} from "@mui/material";
import { SortType } from "../../../../../models/SortType";
import {
  StyledTableCell,
  StyledTableRow,
} from "../../../../../styles/Shipmentstyles";
import {
  PaymentLinkListItem,
  formatPaymentLinkStatus,
} from "../../../../../models/PaymentLinks/PaymentLinks";
import { formatDate } from "../../../../../views/components/common/functions/formatDate";

interface PaymentLinksTableProps {
  maxHeight: number;
  paymentLinks: PaymentLinkListItem[];
  limit: number;
  page_no: number;
  total_count: number;
  sortBy: string;
  sortType: SortType;
  onPageChange: (
    event: React.MouseEvent<HTMLButtonElement> | null,
    page: number,
  ) => void;
  onRowsPerPageChange: (limit: number) => void;
  onSortChange: (column: string, sortType: SortType) => void;
}

const PaymentLinksTable: React.FC<PaymentLinksTableProps> = ({
  maxHeight,
  paymentLinks,
  limit,
  page_no,
  total_count,
  sortBy,
  sortType,
  onPageChange,
  onRowsPerPageChange,
  onSortChange,
}) => {
  const theme = useTheme<Theme>();

  const handleRowsChange = (value: number) => {
    onPageChange(null, 1);
    onRowsPerPageChange(value);
  };

  const renderSortIcon = (property: string) => {
    if (sortBy === property) {
      return sortType === SortType.ASC ? "↑" : "↓";
    }
    return "↑↓";
  };

  const getStatusChipStyles = (status?: string | null) => {
    const normalized = (status || "").toLowerCase();

    if (normalized === "paid") {
      return {
        backgroundColor: "#FFFFFF",
        color: "#15803D",
        border: "1px solid #15803D",
      };
    }

    if (normalized.includes("partial")) {
      return {
        backgroundColor: "#FFFFFF",
        color: "#D97706",
        border: "1px solid #D97706",
      };
    }

    return {
      backgroundColor: "#FFFFFF",
      color: "#DC2626",
      border: "1px solid #DC2626",
    };
  };

  const formatStatusLabel = (status?: string | null) =>
    formatPaymentLinkStatus(status);

  return (
    <>
      <TableContainer sx={{ maxHeight }}>
        <Table stickyHeader>
          <TableHead>
            <TableRow>
              <StyledTableCell
                sx={{
                  textAlign: "center",
                  fontSize: 14,
                  position: "sticky",
                  left: 0,
                  zIndex: 12,
                  boxShadow: "10px 0 20px -12px rgba(115,115,115,0.3);",
                }}
              >
                <TableSortLabel
                  active={sortBy === "order_id"}
                  direction={sortType === SortType.ASC ? "asc" : "desc"}
                  onClick={() =>
                    onSortChange(
                      "order_id",
                      sortBy === "order_id" && sortType === SortType.DESC
                        ? SortType.ASC
                        : SortType.DESC,
                    )
                  }
                  IconComponent={() => (
                    <span>{renderSortIcon("order_id")}</span>
                  )}
                  sx={{ fontSize: "12px" }}
                >
                  Order ID
                </TableSortLabel>
              </StyledTableCell>
              <StyledTableCell sx={{ textAlign: "center", fontSize: 14 }}>
                Payment Link
              </StyledTableCell>
              <StyledTableCell sx={{ textAlign: "center", fontSize: 14 }}>
                <TableSortLabel
                  active={sortBy === "amount"}
                  direction={sortType === SortType.ASC ? "asc" : "desc"}
                  onClick={() =>
                    onSortChange(
                      "amount",
                      sortBy === "amount" && sortType === SortType.DESC
                        ? SortType.ASC
                        : SortType.DESC,
                    )
                  }
                  IconComponent={() => <span>{renderSortIcon("amount")}</span>}
                  sx={{ fontSize: "12px" }}
                >
                  Amount
                </TableSortLabel>
              </StyledTableCell>
              <StyledTableCell sx={{ textAlign: "center", fontSize: 14 }}>
                Status
              </StyledTableCell>
              <StyledTableCell sx={{ textAlign: "center", fontSize: 14 }}>
                <TableSortLabel
                  active={sortBy === "created_date"}
                  direction={sortType === SortType.ASC ? "asc" : "desc"}
                  onClick={() =>
                    onSortChange(
                      "created_date",
                      sortBy === "created_date" && sortType === SortType.DESC
                        ? SortType.ASC
                        : SortType.DESC,
                    )
                  }
                  IconComponent={() => (
                    <span>{renderSortIcon("created_date")}</span>
                  )}
                  sx={{ fontSize: "12px" }}
                >
                  Created Date
                </TableSortLabel>
              </StyledTableCell>
              <StyledTableCell sx={{ textAlign: "center", fontSize: 14 }}>
                Generated By
              </StyledTableCell>
              <StyledTableCell sx={{ textAlign: "center", fontSize: 14 }}>
                Provider
              </StyledTableCell>
              <StyledTableCell sx={{ textAlign: "center", fontSize: 14 }}>
                %
              </StyledTableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {paymentLinks.map((item) => (
              <StyledTableRow key={item.id}>
                <TableCell
                  sx={{
                    textAlign: "center",
                    fontSize: 12,
                    position: "sticky",
                    left: 0,
                    zIndex: 10,
                    boxShadow: "10px 0 20px -12px rgba(115,115,115,0.3);",
                    backgroundColor: theme.palette.background.paper,
                  }}
                >
                  {item.order_id}
                </TableCell>
                <TableCell
                  sx={{
                    textAlign: "left",
                    fontSize: 12,
                    maxWidth: 280,
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                  }}
                >
                  {item.payment_link ? (
                    <Link
                      href={item.payment_link}
                      target="_blank"
                      rel="noopener noreferrer"
                      underline="hover"
                      sx={{ fontSize: 12 }}
                    >
                      {item.payment_link}
                    </Link>
                  ) : (
                    "-"
                  )}
                </TableCell>
                <TableCell sx={{ textAlign: "center", fontSize: 12 }}>
                  {item.amount != null
                    ? `${item.currency || ""} ${item.amount}`.trim()
                    : "-"}
                </TableCell>
                <TableCell
                  sx={{
                    padding: "8px",
                    textAlign: "center",
                    fontSize: "12px",
                  }}
                >
                  <Chip
                    label={formatStatusLabel(item.status)}
                    size="small"
                    variant="outlined"
                    sx={{
                      ...getStatusChipStyles(item.status),
                      fontSize: "0.75rem",
                      borderRadius: "999px",
                      height: "26px",
                      minWidth: "90px",
                      fontWeight: 600,
                    }}
                  />
                </TableCell>
                <TableCell sx={{ textAlign: "center", fontSize: 12 }}>
                  {item.created_date ? formatDate(item.created_date) : "-"}
                </TableCell>
                <TableCell sx={{ textAlign: "center", fontSize: 12 }}>
                  {item.created_by_admin_name || "-"}
                </TableCell>
                <TableCell sx={{ textAlign: "center", fontSize: 12 }}>
                  {item.provider || "-"}
                </TableCell>
                <TableCell sx={{ textAlign: "center", fontSize: 12 }}>
                  {item.percentage ?? "-"}
                </TableCell>
              </StyledTableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
      <TablePagination
        rowsPerPageOptions={[30, 50, 100, 150, 200]}
        component="div"
        count={total_count}
        rowsPerPage={limit}
        page={page_no - 1}
        onPageChange={(event, page) => onPageChange(event, page + 1)}
        onRowsPerPageChange={(event) =>
          handleRowsChange(parseInt(event.target.value, 10))
        }
      />
    </>
  );
};

export default PaymentLinksTable;
